import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { SystemService } from '../../../core/admin/system.service';
import { SystemLogsResponse } from '../../../core/admin/system.types';
import { IconComponent } from '../../../shared/icon/icon';

const PAGE_SIZE = 50;

/**
 * Read-only, paginated viewer for the application's WARNING+ log file.
 *
 * Backed by `GET /api/admin/system-logs/` (see backend `SystemLogsView`). The
 * level filter, search text and page are driven by local signals; every change
 * re-fetches.
 */
@Component({
  selector: 'app-system-logs',
  imports: [
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    IconComponent,
  ],
  templateUrl: './system-logs.html',
  styleUrl: './system-logs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemLogsComponent implements OnInit {
  private readonly systemService = inject(SystemService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly data = signal<SystemLogsResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal(false);

  protected readonly page = signal(1);
  // Empty string = "All levels".
  protected readonly level = signal('');
  // Free text matched against logger + message, typically a request reference.
  protected readonly search = signal('');

  // Levels the backend can filter by; populated from the first response so the
  // dropdown never drifts from the server's known set.
  protected readonly levels = signal<string[]>([]);

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.error.set(false);
    this.cdr.markForCheck();

    this.systemService
      .getSystemLogs(this.page(), PAGE_SIZE, this.level() || undefined, this.search() || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.data.set(res);
          if (res.levels?.length) {
            this.levels.set(res.levels);
          }
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected onLevelChange(value: string): void {
    this.level.set(value);
    this.page.set(1); // a new filter invalidates the current page
    this.load();
  }

  protected onSearch(value: string): void {
    this.search.set(value.trim());
    this.page.set(1);
    this.load();
  }

  protected nextPage(): void {
    if (this.data()?.has_next) {
      this.page.update((p) => p + 1);
      this.load();
    }
  }

  protected previousPage(): void {
    if (this.data()?.has_previous) {
      this.page.update((p) => Math.max(1, p - 1));
      this.load();
    }
  }
}
